import { useEffect, useRef } from 'react';
import { useSetRecoilState } from 'recoil';

import { COLOR, LAYOUT, Variation } from '../../../types/globalSearch';

import styled from '@emotion/styled';
import Dialog from '../../../designSystem/components/Dialog';
import { globalVariables } from '../../../designSystem/constants';

import SearchInput from '../SearchInput';
import SearchSuggestionContent from '../SearchSuggestionContent';
import { searchTerm } from '../recoil';

interface Props {
  isOpen: boolean;
  variations: Variation;
  placeholder: string;
  onDialogToggle: (isOpen: boolean) => void;
  onPressKeyboard: (key: string) => void;
  onSearch: () => void;
}

function SearchSuggestionDialog({
  isOpen,
  variations,
  placeholder,
  onDialogToggle,
  onPressKeyboard,
  onSearch,
}: Props) {
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const setSearchKeyword = useSetRecoilState(searchTerm);
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  const defaultVariations: Variation = {
    layout: LAYOUT.header,
    color: COLOR.white,
  };

  const handleDialogClose = () => {
    setSearchKeyword('');
    onDialogToggle(false);
  };

  const handleClearInput = () => {
    hiddenInputRef.current?.focus();
    searchInputRef.current?.focus();
  };

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  if (!isOpen) {
    return null;
  }

  const titleOptionElement = (
    <TitleOptionWrapper>
      <CloseButton type="button" onClick={handleDialogClose} aria-label="닫기">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M18 6L6 18M6 6l12 12"
            stroke={globalVariables.gray_800}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </CloseButton>
      <SearchInputContainer>
        <SearchInput
          ref={searchInputRef}
          variations={defaultVariations}
          placeholder={placeholder}
          popupSide
          onDialogToggle={onDialogToggle}
          onPressKeyboard={onPressKeyboard}
          onClearInput={handleClearInput}
          onSearch={onSearch}
        />
        <HiddenInput tabIndex={-1} ref={hiddenInputRef} />
      </SearchInputContainer>
    </TitleOptionWrapper>
  );

  return (
    <DialogContainer
      isOpen={isOpen}
      titleText=" "
      titleOptionItem={titleOptionElement}
      toggle={() => {}}
      onCloseClick={handleDialogClose}
    >
      <SearchSuggestionContentContainer>
        <SearchSuggestionContent variations={variations} />
      </SearchSuggestionContentContainer>
    </DialogContainer>
  );
}

export default SearchSuggestionDialog;

const DialogContainer = styled(Dialog.Mobile)`
  .mrt-dialog-content {
    padding: 0;
    margin-top: 56px;
  }
  .mrt-dialog-title {
    > div:first-of-type {
      border-bottom: 0;

      > img {
        display: none;
      }

      > span:first-of-type {
        display: none;
      }
    }
  }

  z-index: ${globalVariables.z_dialog};
`;

const TitleOptionWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 8px;
`;

const SearchInputContainer = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;

  > div {
    height: 44px;
  }
`;

const CloseButton = styled.button`
  margin: 0;
  padding: 4px;
  appearance: none;
  border: none;
  outline: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SearchSuggestionContentContainer = styled.div``;

const HiddenInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  opacity: 0;
`;
